import { computeHoursFromTimeRange } from "@/lib/employeeTimes";
import {
  listWorkTimeAssignments,
  listWorkTimeModels,
  normalizeModelDay,
  type WorkTimeModelDay,
} from "@/lib/workTimeModels";

export type DayEntryRowKind = "work" | "break";

/** Modell-Wochentag: 1 = Montag … 7 = Sonntag */
export function isoDateToModelWeekday(iso: string): number {
  const d = new Date(`${iso}T12:00:00`);
  const js = d.getDay();
  return js === 0 ? 7 : js;
}

export async function resolveEmployeeWorkDay(
  userLogin: string,
  workDateIso: string,
): Promise<WorkTimeModelDay | null> {
  const login = userLogin.trim().toLowerCase();
  if (!login) return null;
  const [models, assignments] = await Promise.all([
    listWorkTimeModels().catch(() => []),
    listWorkTimeAssignments().catch(() => []),
  ]);
  const assignment = assignments.find(
    (a) => a.user_login.trim().toLowerCase() === login,
  );
  const model =
    (assignment && models.find((m) => m.id === assignment.model_id)) ||
    models.find((m) => m.is_default);
  if (!model) return null;
  const weekday = isoDateToModelWeekday(workDateIso);
  const day = model.days.find((d) => d.weekday === weekday);
  return day ? normalizeModelDay(day) : null;
}

export function workDayStartTime(day: WorkTimeModelDay | null): string {
  if (!day?.is_workday || !day.time_from) return "";
  return day.time_from.slice(0, 5);
}

export function breakDurationMinutes(day: WorkTimeModelDay | null): number {
  if (!day?.break_from || !day.break_to) return 30;
  return Math.round(
    computeHoursFromTimeRange(day.break_from.slice(0, 5), day.break_to.slice(0, 5)) * 60,
  );
}

export function addMinutesToTimeHHMM(time: string, minutes: number): string {
  const parts = time.slice(0, 5).split(":").map(Number);
  const h = parts[0] ?? 0;
  const m = parts[1] ?? 0;
  let total = h * 60 + m + minutes;
  while (total < 0) total += 24 * 60;
  total %= 24 * 60;
  return `${String(Math.floor(total / 60)).padStart(2, "0")}:${String(total % 60).padStart(2, "0")}`;
}

interface TimedRow {
  timeFrom: string;
  timeTo: string;
}

export function rowHasTimes(row: TimedRow): boolean {
  return Boolean(row.timeFrom && row.timeTo);
}

export function compareTimeHHMM(a: string, b: string): number {
  const [ah = 0, am = 0] = a.slice(0, 5).split(":").map(Number);
  const [bh = 0, bm = 0] = b.slice(0, 5).split(":").map(Number);
  return ah * 60 + am - (bh * 60 + bm);
}

export function isUnassignedGapFillRow(row: {
  rowKind: DayEntryRowKind;
  id?: number;
  project: unknown;
  timeFrom: string;
  timeTo: string;
}): boolean {
  return (
    row.rowKind === "work" &&
    !row.id &&
    !row.project &&
    Boolean(row.timeFrom && row.timeTo)
  );
}

export function rowHasContent(row: { id?: number } & TimedRow): boolean {
  return rowHasTimes(row) || Boolean(row.id);
}

/**
 * Folgezeilen dürfen später beginnen (Lücke). Liegt Von vor dem Bis der
 * vorherigen Zeile, wird Von auf dieses Bis gesetzt, damit keine Zeit doppelt
 * erfasst wird. Die Dauer bleibt dabei erhalten.
 */
export function resolveDayEntryOverlaps<T extends DayEntryTimeFields & {
  clientKey: string;
  rowKind: DayEntryRowKind;
  id?: number;
  project: unknown;
  markedDelete?: boolean;
  /** Hero-/gesperrte Zeilen: Zeiten nicht umschreiben. */
  readOnly?: boolean;
}>(rows: T[]): T[] {
  const visible = rows.filter((r) => !r.markedDelete);
  const patchByKey = new Map<string, DayEntryTimeFields>();

  for (let i = 1; i < visible.length; i++) {
    const prev = { ...visible[i - 1]!, ...patchByKey.get(visible[i - 1]!.clientKey) };
    const curr = { ...visible[i]!, ...patchByKey.get(visible[i]!.clientKey) };

    // Hero-Zeiten und andere read-only Zeilen behalten ihre Originalzeiten.
    if (curr.readOnly) continue;

    if (!rowHasTimes(prev)) continue;
    const prevEnd = prev.timeTo.slice(0, 5);
    const currFrom = curr.timeFrom.slice(0, 5);

    const overlaps = Boolean(currFrom && compareTimeHHMM(currFrom, prevEnd) < 0);
    const missingStart = curr.rowKind === "work" && !rowHasContent(curr) && !currFrom;

    // Späteres Von ist eine gewollte Lücke und bleibt stehen.
    if (!overlaps && !missingStart) continue;

    if (rowHasTimes(curr)) {
      const hours = computeHoursFromTimeRange(curr.timeFrom.slice(0, 5), curr.timeTo.slice(0, 5));
      if (hours > 0) {
        const newTo = addMinutesToTimeHHMM(prevEnd, Math.round(hours * 60));
        patchByKey.set(curr.clientKey, {
          timeFrom: prevEnd,
          timeTo: newTo,
          durationInput: formatDurationInputHours(hours),
        });
      } else {
        patchByKey.set(curr.clientKey, syncDayEntryTimeFields(curr, { timeFrom: prevEnd }));
      }
    } else {
      patchByKey.set(curr.clientKey, {
        timeFrom: prevEnd,
        timeTo: curr.timeTo,
        durationInput: curr.durationInput,
      });
    }
  }

  if (patchByKey.size === 0) return rows;
  return rows.map((r) => {
    const patch = patchByKey.get(r.clientKey);
    return patch ? { ...r, ...patch } : r;
  });
}

/** Lücken zwischen aufeinanderfolgenden Zeilen mit Von/Bis (z. B. 08:30–11:00). */
export function findDayEntryGaps<T extends { clientKey: string } & TimedRow>(
  rows: T[],
): Array<{ afterClientKey: string; timeFrom: string; timeTo: string }> {
  const gaps: Array<{ afterClientKey: string; timeFrom: string; timeTo: string }> = [];
  for (let i = 0; i < rows.length - 1; i++) {
    const prev = rows[i]!;
    const next = rows[i + 1]!;
    if (!rowHasTimes(prev) || !rowHasTimes(next)) continue;
    const timeFrom = prev.timeTo.slice(0, 5);
    const timeTo = next.timeFrom.slice(0, 5);
    if (compareTimeHHMM(timeTo, timeFrom) > 0) {
      gaps.push({ afterClientKey: prev.clientKey, timeFrom, timeTo });
    }
  }
  return gaps;
}

export function findDayEntryOverlapError(rows: TimedRow[]): string | null {
  const timed = rows.filter((r) => rowHasTimes(r));
  for (let i = 1; i < timed.length; i++) {
    const prev = timed[i - 1]!;
    const curr = timed[i]!;
    if (compareTimeHHMM(curr.timeFrom, prev.timeTo) < 0) {
      return `Zeiten überschneiden sich (${prev.timeFrom.slice(0, 5)}–${prev.timeTo.slice(0, 5)} und ${curr.timeFrom.slice(0, 5)}–${curr.timeTo.slice(0, 5)}).`;
    }
  }
  return null;
}

/** Von-Zeit für neuen Arbeits-Eintrag: Tagesstart oder Bis des letzten Eintrags. */
export function suggestNextWorkTimeFrom(
  contentRows: TimedRow[],
  workDay: WorkTimeModelDay | null,
): string {
  if (contentRows.length > 0) {
    const last = contentRows[contentRows.length - 1];
    if (last?.timeTo) return last.timeTo.slice(0, 5);
  }
  return workDayStartTime(workDay);
}

/** Anzeige der Dauer in Stunden (deutsches Komma). */
export function formatDurationInputHours(hours: number): string {
  if (!Number.isFinite(hours) || hours <= 0) return "";
  return hours.toLocaleString("de-DE", { maximumFractionDigits: 2 });
}

/**
 * Dauer-Eingabe: Dezimalstunden (`1,5`) oder Stunden:Minuten (`1:30`).
 */
export function parseDurationInput(input: string): number | null {
  const trimmed = input.trim();
  if (!trimmed) return null;
  if (trimmed.includes(":")) {
    const [rawH, rawM] = trimmed.split(":");
    const h = Number((rawH ?? "").trim());
    const m = Number((rawM ?? "").trim());
    if (!Number.isFinite(h) || !Number.isFinite(m) || h < 0 || m < 0 || m >= 60) {
      return null;
    }
    const total = h + m / 60;
    return total > 0 ? total : null;
  }
  const n = Number(trimmed.replace(",", "."));
  return Number.isFinite(n) && n > 0 ? n : null;
}

export function formatDurationInputFromRange(timeFrom: string, timeTo: string): string {
  if (!timeFrom || !timeTo) return "";
  const hours = computeHoursFromTimeRange(timeFrom, timeTo);
  return hours > 0 ? formatDurationInputHours(hours) : "";
}

export function applyDurationToEndTime(timeFrom: string, durationInput: string): string | null {
  const hours = parseDurationInput(durationInput);
  if (!hours || !timeFrom) return null;
  return addMinutesToTimeHHMM(timeFrom, Math.round(hours * 60));
}

export interface DayEntryTimeFields {
  timeFrom: string;
  timeTo: string;
  durationInput: string;
}

/** Von / Dauer / Bis konsistent halten. */
export function syncDayEntryTimeFields(
  row: DayEntryTimeFields,
  patch: Partial<DayEntryTimeFields>,
): DayEntryTimeFields {
  const next = { ...row, ...patch };

  if (patch.durationInput !== undefined) {
    const applied = applyDurationToEndTime(next.timeFrom, patch.durationInput);
    if (applied) {
      next.timeTo = applied;
      const hours = parseDurationInput(patch.durationInput);
      if (hours) next.durationInput = formatDurationInputHours(hours);
    }
    return next;
  }

  if (
    patch.timeFrom !== undefined &&
    row.timeFrom &&
    row.timeTo &&
    patch.timeTo === undefined &&
    patch.durationInput === undefined
  ) {
    const hours = computeHoursFromTimeRange(row.timeFrom, row.timeTo);
    if (hours > 0) {
      next.timeTo = addMinutesToTimeHHMM(patch.timeFrom, Math.round(hours * 60));
      next.durationInput = formatDurationInputHours(hours);
      return next;
    }
  }

  if (patch.timeTo !== undefined || patch.timeFrom !== undefined) {
    next.durationInput = formatDurationInputFromRange(next.timeFrom, next.timeTo);
  }

  return next;
}

/** Pause: Von = Bis des vorherigen Eintrags, Bis = Von + Pausenlänge aus Modell. */
export function suggestBreakRange(
  contentRows: TimedRow[],
  workDay: WorkTimeModelDay | null,
): { timeFrom: string; timeTo: string } | null {
  const previousEnd =
    contentRows.length > 0
      ? contentRows[contentRows.length - 1]?.timeTo?.slice(0, 5)
      : workDayStartTime(workDay);
  if (!previousEnd) return null;
  const duration = breakDurationMinutes(workDay);
  return {
    timeFrom: previousEnd,
    timeTo: addMinutesToTimeHHMM(previousEnd, duration),
  };
}
